import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface BookRecommendation {
  title: string;
  author: string;
  reason: string;
  isbn13?: string;
  isbn10?: string;
  bookId?: string;
  coverUrl?: string;
  confidenceScore?: number;
  confidence_score?: number;
  matchDetails?: string;
  match_details?: string;
  recommendationType?: 'safe_bet' | 'stretch' | 'wildcard' | 'classic';
  recommendation_type?: 'safe_bet' | 'stretch' | 'wildcard' | 'classic';
  sourceBooks?: Array<{
    id: string;
    title: string;
    reason: string;
  }>;
  source_books?: Array<{
    id: string;
    title: string;
    reason: string;
  }>;
  sessionId?: string;
}

const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
 
export const useBookRecommendations = (refreshKey: number = 0) => {
  return useQuery({
    queryKey: ['bookRecommendations', refreshKey],
    queryFn: async (): Promise<{ recommendations: BookRecommendation[], classicRecommendation: BookRecommendation | null }> => {
      console.log('🔍 Fetching book recommendations...');
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Fetch recently shown recommendations (last 7 days) to avoid repetition
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data: recentRecs } = await supabase
        .from('recommendation_feedback')
        .select('book_title, book_author')
        .eq('user_id', session.user.id)
        .gte('created_at', sevenDaysAgo)
        .limit(100); // Increased from 50 to track more history
      
      const recentlyShown = recentRecs?.map(r => `${r.book_title} by ${r.book_author}`) || [];
      
      // Also get books from TBR and current reading to avoid recommending them
      const { data: userBooks } = await supabase
        .from('user_books')
        .select('books:books!user_books_book_id_fkey(title, authors)')
        .eq('user_id', session.user.id)
        .in('shelf', ['tbr', 'reading']);
      
      const userBooksList = userBooks?.map((ub: any) => 
        `${ub.books?.title} by ${ub.books?.authors?.[0] || 'Unknown'}`
      ).filter(Boolean) || [];
      
      const allExclusions = [...recentlyShown, ...userBooksList];
      console.log(`📋 Excluding ${allExclusions.length} books (${recentlyShown.length} recently shown + ${userBooksList.length} in TBR/reading)`);

      // Add variation instruction when refreshing
      const variationPrompt = refreshKey > 0 
        ? `CRITICAL REFRESH REQUEST: User clicked refresh to see COMPLETELY NEW books. 
        
DO NOT RECOMMEND any books from this extensive exclusion list - they have already been shown or dismissed:
${allExclusions.map((book, i) => `${i + 1}. ${book}`).join('\n')}

Focus on:
- Books from DIFFERENT time periods (mix classics with contemporary)
- DIFFERENT genres or sub-genres than previously shown
- International/translated works if not shown before
- Lesser-known gems that match their taste
- Hidden treasures they might have missed

This is refresh #${refreshKey}, so prioritize maximum variety and freshness.`
        : undefined;

      const { data, error } = await supabase.functions.invoke('recommend-books', {
        body: { 
          excludeRecentlyShown: allExclusions,
          customPrompt: variationPrompt,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        console.error('❌ Error fetching recommendations:', error);
        throw error;
      }

      console.log('✅ Received data from edge function:', data);

      // Data now comes structured - edge function returns 5 regular + 1 classic already filtered
      const recommendations = data.recommendations || [];
      console.log(`📚 Got ${recommendations.length} recommendations (already filtered and enriched)`);

      // Generate session ID for this batch
      const sessionId = crypto.randomUUID();

      // Separate classic from regular (edge function handles this but we display separately)
      const classicRec = recommendations.find((r: any) => 
        r.recommendation_type === 'classic' || r.recommendationType === 'classic'
      );
      const regularRecs = recommendations.filter((r: any) => 
        r.recommendation_type !== 'classic' && r.recommendationType !== 'classic'
      );
      
      console.log(`📚 Display: ${regularRecs.length} regular recommendations + ${classicRec ? '1' : '0'} classic`);

      // Enrich with book data from our database
      const enrichedRecommendations = await enrichWithBookData(regularRecs, sessionId);
      const enrichedClassic = classicRec ? (await enrichWithBookData([classicRec], sessionId))[0] : null;
      console.log(`✨ Enriched ${enrichedRecommendations.length} regular + ${enrichedClassic ? '1' : '0'} classic with book data`);
      
      
      // Client-side safety filter (should already be filtered server-side)
      const exclusionSet = new Set(allExclusions.map(book => normalize(book)));
      const safeFinalRecs = enrichedRecommendations.filter(rec => {
        const key = normalize(`${rec.title} by ${rec.author}`);
        const isExcluded = exclusionSet.has(key);
        if (isExcluded) {
          console.warn(`🚫 Client-side filtered: "${rec.title}" by ${rec.author}`);
        }
        return !isExcluded;
      });
      
      console.log(`📊 Final: ${safeFinalRecs.length} recommendations + ${enrichedClassic ? '1 classic' : 'no classic'}`);

      // Log all displayed recommendations for future exclusion
      const allDisplayed = enrichedClassic ? [...safeFinalRecs, enrichedClassic] : safeFinalRecs;
      if (session.user && allDisplayed.length > 0) {
        const feedbackEntries = allDisplayed.map(rec => ({
          user_id: session.user.id,
          book_id: rec.bookId,
          book_title: rec.title,
          book_author: rec.author,
          isbn13: rec.isbn13,
          isbn10: rec.isbn10,
          feedback_type: 'shown' as const,
          recommendation_reason: rec.reason,
          session_id: sessionId,
        }));
        
        // Log asynchronously, don't await to avoid slowing down UI
        supabase
          .from('recommendation_feedback')
          .insert(feedbackEntries)
          .then(({ error }) => {
            if (error) console.error('Failed to log shown recommendations:', error);
            else console.log(`✅ Logged ${feedbackEntries.length} shown recommendations for future exclusion`);
          });
      }

      return {
        recommendations: safeFinalRecs,
        classicRecommendation: enrichedClassic || null
      };
    },
    staleTime: Infinity, // Never auto-refetch, only when user clicks refresh
    gcTime: 1000 * 60 * 60 * 24, // Keep cache for 24 hours even when unmounted
    refetchOnMount: false, // Don't refetch when navigating back to page
    refetchOnWindowFocus: false, // Don't refetch on window focus
    retry: 1
  });
};

// Helper function to find a better edition of a book when the matched one has incomplete data
async function findBestEdition(title: string, author: string, currentBookId: string): Promise<any | null> {
  console.log(`🔍 Searching for better edition of "${title}" by ${author}...`);
  
  const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const normalizedTitle = normalize(title);
  const normalizedAuthor = normalize(author);
  
  // Search for books with matching title
  const { data: books } = await supabase
    .from('books')
    .select('id, title, authors, cover_url, cover_cache_url, page_count, synopsis, enrichment_status')
    .ilike('title', title)
    .neq('id', currentBookId) // Exclude the current incomplete book
    .limit(10);
  
  if (!books || books.length === 0) {
    console.log(`❌ No alternative editions found for "${title}"`);
    return null;
  }
  
  // Find best match: title + author match with completed enrichment and good data
  const candidates = books.filter(b => {
    const titleMatches = normalize(b.title) === normalizedTitle;
    const authorMatches = b.authors?.some(a => normalize(a).includes(normalizedAuthor)) || false;
    const hasCompleteData = b.enrichment_status === 'completed' && b.page_count && b.synopsis;
    
    return titleMatches && authorMatches && hasCompleteData;
  });
  
  if (candidates.length > 0) {
    // Prefer book with cover
    const withCover = candidates.find(b => b.cover_url || b.cover_cache_url);
    const bestEdition = withCover || candidates[0];
    console.log(`✅ Found better edition: ${bestEdition.id} (page_count: ${bestEdition.page_count}, has synopsis: ${!!bestEdition.synopsis})`);
    return bestEdition;
  }
  
  console.log(`⚠️ Found ${books.length} similar books but none with complete data`);
  return null;
}

async function enrichWithBookData(recommendations: BookRecommendation[], sessionId: string): Promise<BookRecommendation[]> {
  // Helper function to normalize strings for comparison
  const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

  // Fetch user's reading history for client-side filtering (defense in depth)
  const { data: { user } } = await supabase.auth.getUser();
  
  const myBookIds = new Set<string>();
  const myTitles = new Set<string>();
  const myIsbns = new Set<string>();
  
  if (user) {
    const { data: userHistory } = await supabase
      .from('user_books')
      .select(`
        book_id,
        books!inner(id, title, isbn13, isbn10)
      `)
      .eq('user_id', user.id);

    if (userHistory) {
      userHistory.forEach((ub: any) => {
        const book = ub.books;
        myBookIds.add(ub.book_id);
        if (book.title) myTitles.add(normalize(book.title));
        if (book.isbn13) myIsbns.add(book.isbn13);
        if (book.isbn10) myIsbns.add(book.isbn10);
      });
    }
  }

  console.debug(`Client-side filter: ${myTitles.size} titles, ${myIsbns.size} ISBNs in history`);

  const enrichedRecommendations: BookRecommendation[] = [];

  for (const rec of recommendations) {
    // Client-side filter: skip if already in user's library
    const titleMatch = myTitles.has(normalize(rec.title));
    const isbn13Match = rec.isbn13 && myIsbns.has(rec.isbn13);
    const isbn10Match = rec.isbn10 && myIsbns.has(rec.isbn10);
    
    if (titleMatch || isbn13Match || isbn10Match) {
      console.debug(`Client filtered already-read: ${rec.title}`);
      continue;
    }

    try {
      let book = null;
      let matchConfidence: 'high' | 'low' = 'low';

        // Try ISBN-13 first (most reliable, but double-check title/author)
        if (rec.isbn13) {
          const { data } = await supabase
            .from('books')
            .select('id, title, authors, cover_url, cover_cache_url, isbn13, page_count, synopsis, enrichment_status')
            .eq('isbn13', rec.isbn13)
            .single();
          if (data) {
            const titleMatches = normalize(data.title) === normalize(rec.title);
            const authorMatches = rec.author
              ? data.authors?.some((a: string) => normalize(a) === normalize(rec.author!))
              : false;

            if (titleMatches || authorMatches) {
              book = data;
              matchConfidence = 'high';
            } else {
              console.warn(`⚠️ ISBN13 match rejected due to title/author mismatch: rec="${rec.title}" by ${rec.author}, db="${data.title}"`);
            }
          }
        }

        // Fallback to ISBN-10 (also validated against title/author)
        if (!book && rec.isbn10) {
          const { data } = await supabase
            .from('books')
            .select('id, title, authors, cover_url, cover_cache_url, isbn10, page_count, synopsis, enrichment_status')
            .eq('isbn10', rec.isbn10)
            .single();
          if (data) {
            const titleMatches = normalize(data.title) === normalize(rec.title);
            const authorMatches = rec.author
              ? data.authors?.some((a: string) => normalize(a) === normalize(rec.author!))
              : false;

            if (titleMatches || authorMatches) {
              book = data;
              matchConfidence = 'high';
            } else {
              console.warn(`⚠️ ISBN10 match rejected due to title/author mismatch: rec="${rec.title}" by ${rec.author}, db="${data.title}"`);
            }
          }
        }

        // Try exact title match with author verification
        if (!book && rec.title && rec.author) {
          const { data: books } = await supabase
            .from('books')
            .select('id, title, authors, cover_url, cover_cache_url, page_count, synopsis, enrichment_status')
            .ilike('title', rec.title)
            .limit(5);

          // Find best author match
          if (books && books.length > 0) {
            const authorLower = rec.author.toLowerCase();
            book = books.find(b => 
              b.authors?.some(a => a.toLowerCase().includes(authorLower))
            );
            
            if (book) {
              matchConfidence = 'high';
            }
          }
        }

        // If we found a book but it has no_data_found status, try to find a better edition
        if (book && book.enrichment_status === 'no_data_found') {
          console.log(`⚠️ Found book "${book.title}" but enrichment status is no_data_found, searching for better edition...`);
          const betterEdition = await findBestEdition(book.title, rec.author, book.id);
          
          if (betterEdition) {
            console.log(`✅ Using better edition: ${betterEdition.id} instead of ${book.id}`);
            book = betterEdition;
          } else {
            console.log(`❌ No better edition found, filtering out "${rec.title}"`);
            continue; // Skip this recommendation - no complete data available
          }
        }

      if (book && matchConfidence === 'high') {
        // Check if book is already in user's library (any shelf) - skip if so
        if (user) {
          const { data: userBook } = await supabase
            .from('user_books')
            .select('shelf')
            .eq('user_id', user.id)
            .eq('book_id', book.id)
            .single();

          if (userBook) {
            // Book is already in user's library - skip it entirely
            console.debug(`Filtered post-enrichment: ${rec.title} (shelf: ${userBook.shelf})`);
            continue;
          }
        }

        // Validate sourceBooks - loosen strict title matching
        let validatedSourceBooks = rec.source_books || rec.sourceBooks;
        if (validatedSourceBooks && validatedSourceBooks.length > 0) {
          // Just ensure source book isn't same as recommendation
          validatedSourceBooks = validatedSourceBooks.filter(sb => 
            normalize(sb.title) !== normalize(rec.title)
          );
          if (validatedSourceBooks.length === 0) {
            validatedSourceBooks = undefined;
          }
        }

        enrichedRecommendations.push({
          ...rec,
          bookId: book.id,
          coverUrl: book.cover_cache_url || book.cover_url,
          confidenceScore: rec.confidence_score || rec.confidenceScore,
          matchDetails: rec.match_details || rec.matchDetails,
          recommendationType: rec.recommendation_type || rec.recommendationType,
          sourceBooks: validatedSourceBooks,
          sessionId,
        });
      } else {
        // No confident match - create a minimal book record automatically
        console.log(`📚 Auto-creating book record for: "${rec.title}" by ${rec.author}`);
        
        try {
          const { data: createdBook, error: createError } = await supabase
            .from('books')
            .insert({
              title: rec.title,
              authors: rec.author ? [rec.author] : [],
              isbn13: rec.isbn13 || null,
              isbn10: rec.isbn10 || null,
              cover_url: rec.coverUrl || null,
              source: 'ai_recommendation',
              language: 'en',
              data_sources: { ai_recommendation: true },
            })
            .select('id, title, authors, cover_url, cover_cache_url')
            .single();

          if (createError) {
            console.error('Error auto-creating book:', createError);
            // Still include without bookId if creation fails
            let validatedSourceBooks = rec.source_books || rec.sourceBooks;
            if (validatedSourceBooks && validatedSourceBooks.length > 0) {
              validatedSourceBooks = validatedSourceBooks.filter(sb => 
                normalize(sb.title) !== normalize(rec.title)
              );
              if (validatedSourceBooks.length === 0) {
                validatedSourceBooks = undefined;
              }
            }

            enrichedRecommendations.push({
              ...rec,
              sourceBooks: validatedSourceBooks,
              sessionId,
            });
          } else if (createdBook) {
            console.log(`✅ Successfully created book with ID: ${createdBook.id}`);
            
            // Double-check this book isn't in user's library (defensive)
            if (user) {
              const { data: userBook } = await supabase
                .from('user_books')
                .select('shelf')
                .eq('user_id', user.id)
                .eq('book_id', createdBook.id)
                .single();

              if (userBook) {
                console.debug(`Skipping auto-created book already in library: ${rec.title} (shelf: ${userBook.shelf})`);
                continue;
              }
            }
            
            let validatedSourceBooks = rec.source_books || rec.sourceBooks;
            if (validatedSourceBooks && validatedSourceBooks.length > 0) {
              validatedSourceBooks = validatedSourceBooks.filter(sb => 
                normalize(sb.title) !== normalize(rec.title)
              );
              if (validatedSourceBooks.length === 0) {
                validatedSourceBooks = undefined;
              }
            }

            enrichedRecommendations.push({
              ...rec,
              bookId: createdBook.id,
              coverUrl: createdBook.cover_url || rec.coverUrl,
              confidenceScore: rec.confidence_score || rec.confidenceScore,
              matchDetails: rec.match_details || rec.matchDetails,
              recommendationType: rec.recommendation_type || rec.recommendationType,
              sourceBooks: validatedSourceBooks,
              sessionId,
            });
          }
        } catch (err) {
          console.error('Exception creating book:', err);
          // Fallback: include without bookId
          let validatedSourceBooks = rec.source_books || rec.sourceBooks;
          if (validatedSourceBooks && validatedSourceBooks.length > 0) {
            validatedSourceBooks = validatedSourceBooks.filter(sb => 
              normalize(sb.title) !== normalize(rec.title)
            );
            if (validatedSourceBooks.length === 0) {
              validatedSourceBooks = undefined;
            }
          }

          enrichedRecommendations.push({
            ...rec,
            sourceBooks: validatedSourceBooks,
            sessionId,
          });
        }
      }
    } catch (error) {
      console.error('Error enriching book:', error);
    }
  }

  console.debug(`Client-side: ${enrichedRecommendations.length} recommendations after filtering`);
  return enrichedRecommendations;
}
